import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from './cn';

/**
 * Botón del sistema.
 *
 * Punto de partida: 573 elementos `<button>` con clases escritas a mano, cada uno
 * redefiniendo tamaño, radio, color, foco y estado de Loads (UX_AUDIT H-15). El
 * coste real de eso no era la repetición: era que arreglar el foco de teclado o
 * el color de marca exigía 573 ediciones, así que no se arreglaba nunca.
 *
 * Decisiones que esta primitiva fija de una vez:
 *
 * - El foco NO se declara aquí. Lo pone la capa base sobre `:focus-visible`
 *   (`src/index.css`), para todo elemento enfocable de la app, distinguiendo
 *   teclado de ratón. Un botón que no pase por esta primitiva también lo Receives.
 * - `--primary-color` instead of `#003B71`: el color configurado en
 *   Configuración se aplica de verdad, y en modo oscuro usa su variante clara
 *   (los 625 hex fijos quedaban navy sobre fondo oscuro).
 * - `disabled` mientras Loads: un envío que no is blocked a sí mismo produjo un
 *   doble envío real en the form público de cotización (ver memoria de
 *   proyecto). Aquí `loading` implica `disabled`, no depende de recordarlo.
 * - Ancho mínimo estable entre reposo y Loads: the button no encoge al aparecer
 *   el spinner, así que el cursor no se queda apuntando a otra cosa.
 */

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
type Size = 'sm' | 'md' | 'lg';

const base =
  'relative inline-flex items-center justify-center gap-2 font-medium ' +
  'rounded-control transition-colors duration-[var(--duration-quick)] ' +
  'disabled:opacity-55 disabled:cursor-not-allowed disabled:pointer-events-none ' +
  'select-none whitespace-nowrap';

const sizes: Record<Size, string> = {
  sm: 'h-8 px-3 text-[0.8125rem]',
  md: 'h-10 px-4 text-sm',
  lg: 'h-11 px-5 text-sm',
};

/**
 * Brand variants use `style` instead of a class: `--primary-color` is written at
 * runtime and Tailwind can't generate a utility from a value that doesn't exist
 * at compile time.
 */
const variantClass: Record<Variant, string> = {
  primary: 'text-white shadow-raised hover:brightness-110 active:brightness-95',
  secondary:
    'bg-surface text-ink border border-hairline-strong hover:bg-surface-sunken active:bg-surface-sunken',
  ghost: 'text-ink-secondary hover:bg-surface-sunken hover:text-ink',
  danger: 'text-white shadow-raised hover:brightness-110 active:brightness-95',
  link: 'text-brand-600 underline-offset-4 hover:underline px-0 h-auto',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Bloquea the button y muestra el spinner. Implica `disabled`. */
  loading?: boolean;
  /** Texto mientras `loading`. by default conserva el original. */
  loadingText?: string;
  icon?: React.ComponentType<{ size?: number; className?: string }>;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    loadingText,
    icon: Icon,
    iconPosition = 'left',
    fullWidth,
    className,
    children,
    disabled,
    style,
    type = 'button',
    ...rest
  },
  ref,
) {
  const brandStyle: React.CSSProperties =
    variant === 'primary'
      ? { backgroundColor: 'var(--primary-color)' }
      : variant === 'danger'
        ? { backgroundColor: 'var(--color-negative)' }
        : {};

  const iconSize = size === 'sm' ? 14 : 16;

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      // Anuncia el trabajo en curso a un lector de pantalla; sin esto, `loading`
      // sería una señal exclusivamente visual.
      aria-busy={loading || undefined}
      data-on-brand={variant === 'primary' || variant === 'danger' ? '' : undefined}
      className={cn(
        base,
        variant !== 'link' && sizes[size],
        variantClass[variant],
        fullWidth && 'w-full',
        className,
      )}
      style={{ ...brandStyle, ...style }}
      {...rest}
    >
      {loading ? (
        <Loader2 size={iconSize} className="animate-spin shrink-0" aria-hidden="true" />
      ) : (
        Icon && iconPosition === 'left' && <Icon size={iconSize} className="shrink-0" aria-hidden="true" />
      )}
      {loading && loadingText ? loadingText : children}
      {!loading && Icon && iconPosition === 'right' && (
        <Icon size={iconSize} className="shrink-0" aria-hidden="true" />
      )}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonProps, 'children' | 'icon' | 'fullWidth'> {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  /**
   * Obligatorio: es el nombre accesible del botón y su tooltip.
   *
   * En the status inicial había 25 `aria-label` y 17 `title` para 573 botones,
   * muchos solo-icono: en the table de Operaciones cada fila terminaba en tres
   * iconos sin nombre (UX_AUDIT H-23), y un lector de pantalla anunciaba
   * "botón, botón, botón". Que TypeScript lo exija es lo que evita que vuelva a
   * pasar -- una convención escrita en una guía no lo evitó.
   */
  label: string;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon: Icon, label, size = 'md', variant = 'ghost', className, loading, ...rest },
  ref,
) {
  const box = size === 'sm' ? 'h-8 w-8' : size === 'lg' ? 'h-11 w-11' : 'h-9 w-9';
  const iconSize = size === 'sm' ? 15 : 17;

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      loading={loading}
      aria-label={label}
      data-tooltip={label}
      className={cn(box, 'px-0', className)}
      {...rest}
    >
      {!loading && <Icon size={iconSize} aria-hidden="true" />}
    </Button>
  );
});

export default Button;
